// Shape Lab: name, count, fold, sort, measure. Every answer is checked against the figure.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import type { GameModule, GameContext } from '@shell/types';
import { makeNumpad, makeChoices } from '@games/common/numpad';
import { makeActivity, MIRROR_ANGLES, reflect, type Activity, type Pt, type ShapeDef } from '@problems/shapes';

const PER_ROUND = 6;

function mount(host: HTMLElement, ctx: GameContext) {
  let level = ctx.currentLevel();
  let act: Activity = makeActivity(level, ctx.rng);
  let n = 0;
  let attempts = 0, correct = 0;
  let hinted = false, missed = false, locked = false;
  const t0 = performance.now();
  let tProblem = performance.now();
  // interaction state
  let mirror = 0;                     // index into MIRROR_ANGLES
  let counted = new Set<string>();    // shaded grid cells
  let plotted: Pt | null = null;
  let protractorHand = 0;             // degrees she dragged the reading hand to

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Shape ', h('strong', null, '1'), ` of ${PER_ROUND}`), h('button.btn.small.ghost', { onclick: hint }, 'Hint'));
  const stage = h('div');
  const question = h('p.center', { style: { minHeight: '44px', fontWeight: '800', fontSize: '19px' } });
  const controls = h('div.stack');
  host.append(head, stage, question, controls);
  const S = makeSurface(stage, { aspect: 0.78, onResize: () => draw() });

  const unPointer = attachPointer(S.canvas, {
    down(p) { if (act.kind === 'symmetry' || act.kind === 'protractor') dragTo(p.x, p.y); },
    move(p, e) { if ((e.buttons || e.pointerType !== 'mouse') && (act.kind === 'symmetry' || act.kind === 'protractor')) dragTo(p.x, p.y); },
    tap(p) {
      if (locked) return;
      if (act.kind === 'area-count' || act.kind === 'perimeter') { const cell = gridCellAt(p.x, p.y); if (cell) { const k = cell.join(','); if (counted.has(k)) counted.delete(k); else counted.add(k); draw(); ctx.say(`${counted.size} marked.`); } }
      if (act.kind === 'plot') { const cell = planePointAt(p.x, p.y); if (cell) { plotted = cell; draw(); } }
    },
  });

  function dragTo(x: number, y: number) {
    if (locked) return;
    const { cx, cy } = center();
    if (act.kind === 'symmetry') {
      const a = Math.atan2(x - cx, -(y - cy)); // 0 = up
      let best = 0, bd = 10;
      MIRROR_ANGLES.forEach((m, i) => { for (const off of [0, Math.PI, -Math.PI]) { const d = Math.abs(((a - m - off + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI); if (d < bd) { bd = d; best = i; } } });
      mirror = best; draw();
    } else if (act.kind === 'protractor') {
      const deg = Math.round((Math.atan2(cy - y, x - cx) * 180 / Math.PI + 360) % 360);
      protractorHand = Math.max(0, Math.min(180, deg)); draw();
    }
  }

  function center() { return { cx: S.w / 2, cy: S.h / 2 }; }
  function shapeRadius() { return Math.min(S.w, S.h) * 0.32; }

  // ---- grid helpers ----
  function gridGeom() {
    const cells = (act as any).cells as Pt[];
    const maxX = Math.max(...cells.map(c => c[0])) + 1, maxY = Math.max(...cells.map(c => c[1])) + 1;
    const unit = Math.min((S.w - 40) / Math.max(maxX, 4), (S.h - 50) / Math.max(maxY, 4));
    const ox = (S.w - unit * maxX) / 2, oy = (S.h - unit * maxY) / 2 + 8;
    return { unit, ox, oy, maxX, maxY, cells };
  }
  function gridCellAt(x: number, y: number): Pt | null {
    if (act.kind !== 'area-count' && act.kind !== 'perimeter') return null;
    const g = gridGeom();
    const cx = Math.floor((x - g.ox) / g.unit), cy = Math.floor((y - g.oy) / g.unit);
    return g.cells.some(c => c[0] === cx && c[1] === cy) ? [cx, cy] : null;
  }
  function planeGeom() {
    const size = (act as any).size as number;
    const unit = Math.min(S.w - 60, S.h - 50) / size;
    const ox = 40, oy = S.h - 30;
    return { unit, ox, oy, size };
  }
  function planePointAt(x: number, y: number): Pt | null {
    const g = planeGeom();
    const px = Math.round((x - g.ox) / g.unit), py = Math.round((g.oy - y) / g.unit);
    if (px < 0 || py < 0 || px > g.size || py > g.size) return null;
    return [px, py];
  }

  // ---- lifecycle ----
  function newActivity() {
    level = ctx.currentLevel();
    act = makeActivity(level, ctx.rng);
    hinted = false; missed = false; locked = false; mirror = 2; counted = new Set(); plotted = null; protractorHand = 0;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    renderControls();
    draw();
  }

  function renderControls() {
    clear(controls);
    switch (act.kind) {
      case 'name': {
        question.textContent = 'What shape is this?';
        const a = act;
        const ch = makeChoices(a.choices, (v, b) => answer(v === a.answer ? 1 : 0, b, a.answer));
        controls.append(ch.el); break;
      }
      case 'count': {
        question.textContent = `How many ${act.what} does it have?`;
        const a = act;
        const ch = makeChoices(a.choices, (v, b) => answer(Number(v) === a.answer ? 1 : 0, b, String(a.answer)));
        controls.append(ch.el); break;
      }
      case 'symmetry': {
        question.textContent = `Drag the mirror line so both halves of the ${act.shape.name} match.`;
        controls.append(h('button.btn.primary.block', { onclick: () => { const ok = act.kind === 'symmetry' && act.valid.includes(MIRROR_ANGLES[mirror]); answer(ok ? 1 : 0, null, act.kind === 'symmetry' ? (act.valid.length ? 'a line that folds the shape onto itself' : 'no line works: this shape has no mirror line') : ''); } }, 'This line works'));
        if (act.valid.length === 0) controls.append(h('button.btn.block', { onclick: () => answer(1, null, 'right, no mirror line works') }, 'No line works'));
        else controls.append(h('button.btn.ghost.block', { onclick: () => answer(0, null, 'this shape does have a mirror line') }, 'No line works'));
        break;
      }
      case 'angle-sort': {
        question.textContent = 'Is this angle smaller than, exactly, or bigger than a right angle?';
        const opts = ['smaller than a right angle', 'a right angle', 'bigger than a right angle'];
        controls.append(h('div.stack', null, ...opts.map(o => h('button.btn.block', { onclick: (e: Event) => answer(o === (act as any).answer ? 1 : 0, e.currentTarget as HTMLButtonElement, (act as any).answer) }, o.charAt(0).toUpperCase() + o.slice(1)))));
        break;
      }
      case 'perimeter': case 'area-count': case 'area-rect': case 'area-tri': case 'protractor': case 'distance': case 'volume': {
        question.textContent = act.kind === 'perimeter' ? 'How many unit edges go around the outside?' : act.kind === 'area-count' ? 'How many squares are inside? Tap squares to mark them.' : act.kind === 'area-rect' ? 'What is the area in square units?' : act.kind === 'area-tri' ? 'What is the area of the triangle in square units?' : act.kind === 'protractor' ? 'Drag the hand to the ray, then type the angle in degrees.' : act.kind === 'distance' ? 'How many units is it from A to B along the grid lines?' : 'How many unit cubes fill the box?';
        const np = makeNumpad({ onEnter: v => { const ok = Math.abs(v - (act as any).answer) < 1e-9 || (act.kind === 'protractor' && Math.abs(v - (act as any).answer) <= 5); answer(ok ? 1 : 0, null, String((act as any).answer)); if (!ok && !locked) np.clear(); }, enterLabel: 'Check' });
        controls.append(np.el); break;
      }
      case 'plot': {
        question.textContent = `Tap the point (${act.x}, ${act.y}).`;
        controls.append(h('button.btn.primary.block', { onclick: () => { if (!plotted) { ctx.say('Tap a grid point first.'); return; } const ok = plotted[0] === (act as any).x && plotted[1] === (act as any).y; answer(ok ? 1 : 0, null, `(${(act as any).x}, ${(act as any).y}): go ${(act as any).x} right, then ${(act as any).y} up`); } }, 'That is the point'));
        break;
      }
    }
  }

  function answer(ok: number, btn: HTMLButtonElement | null, right: string) {
    if (locked) return;
    const ms = performance.now() - tProblem;
    if (ok) {
      locked = true;
      if (!missed) { attempts++; correct++; ctx.report({ skill: 'geometry', level, correct: true, hintUsed: hinted, ms, problemId: `shape:${act.kind}` }); }
      if (btn) btn.classList.add('good');
      ctx.sound('ding');
      ctx.say(explain());
      n++;
      if (n >= PER_ROUND) { setTimeout(finish, 1200); return; }
      setTimeout(newActivity, 1400);
    } else {
      if (!missed) { attempts++; missed = true; ctx.report({ skill: 'geometry', level, correct: false, hintUsed: hinted, ms, problemId: `shape:${act.kind}` }); }
      if (btn) { btn.classList.add('bad'); setTimeout(() => btn.classList.remove('bad'), 500); }
      ctx.sound('buzz');
      ctx.say(`Not quite. Think about ${right}. Try again.`);
    }
  }

  function explain(): string {
    switch (act.kind) {
      case 'name': return `A ${act.answer}.`;
      case 'count': return `${act.answer} ${act.what}.`;
      case 'symmetry': return act.valid.length ? `Fold along that line and the halves match.` : `Right, nothing matches, so no mirror line.`;
      case 'angle-sort': return act.degrees === 90 ? 'A right angle is 90 degrees, like a square corner.' : act.degrees < 90 ? `${act.degrees} degrees is smaller than 90.` : `${act.degrees} degrees is bigger than 90.`;
      case 'perimeter': return `${act.answer} edges around the outside.`;
      case 'area-count': return `${act.answer} squares inside.`;
      case 'area-rect': return `Area = ${act.w} x ${act.h} = ${act.answer}.`;
      case 'area-tri': return `Half of ${act.a} x ${act.b} = ${act.answer}.`;
      case 'protractor': return `${act.degrees} degrees.`;
      case 'plot': return `Across ${act.x}, up ${act.y}.`;
      case 'distance': return `${Math.abs(act.a[0] - act.b[0])} across plus ${Math.abs(act.a[1] - act.b[1])} up is ${act.answer}.`;
      case 'volume': return `${act.l} x ${act.w} = ${act.l * act.w} per layer, times ${act.h} layers = ${act.answer}.`;
    }
  }

  function hint() {
    if (locked) return;
    hinted = true;
    const t: Record<string, string> = {
      name: 'Count the sides first.', count: 'Trace each side with your finger.', symmetry: 'Try the up-and-down line first, then the diagonals.',
      'angle-sort': 'A right angle makes a perfect square corner.', perimeter: 'Walk around the outside and count every little edge.', 'area-count': 'Tap each square once; the count is your answer.',
      'area-rect': 'Area of a rectangle is width times height.', 'area-tri': 'A right triangle is half of a rectangle.', protractor: 'Read where the ray crosses the numbers on the arc.',
      plot: 'The first number goes across, the second goes up.', distance: 'Count across, then count up, then add.', volume: 'Count one layer, then multiply by the number of layers.',
    };
    ctx.say(`Hint: ${t[act.kind]}`);
  }

  function finish() {
    if (attempts === correct && level >= 5) ctx.charm('charm.shapes');
    ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 });
  }

  // ---- drawing ----
  function drawShape(c: CanvasRenderingContext2D, shape: ShapeDef, cx: number, cy: number, r: number, fill: string) {
    c.fillStyle = fill; c.strokeStyle = '#2a2a2e'; c.lineWidth = 3;
    c.beginPath();
    if (shape.poly === 'circle') c.arc(cx, cy, r * 0.9, 0, 7);
    else if (shape.poly === 'oval') c.ellipse(cx, cy, r, r * 0.6, 0, 0, 7);
    else { shape.poly.forEach((p, i) => { const x = cx + p[0] * r, y = cy + p[1] * r; if (i) c.lineTo(x, y); else c.moveTo(x, y); }); c.closePath(); }
    c.fill(); c.stroke();
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    if (W < 16) return;
    c.fillStyle = '#efe9db'; c.fillRect(0, 0, W, H);
    const { cx, cy } = center();
    const r = shapeRadius();
    switch (act.kind) {
      case 'name': case 'count': drawShape(c, act.shape, cx, cy, r, '#4f8fe6'); break;
      case 'symmetry': {
        drawShape(c, act.shape, cx, cy, r, '#9b6be6');
        // reflected ghost to show whether halves match
        const a = MIRROR_ANGLES[mirror];
        const poly = act.shape.poly as Pt[];
        c.save(); c.globalAlpha = 0.35; c.fillStyle = '#f5c542'; c.beginPath();
        poly.forEach((p, i) => { const q = reflect(p, a); const x = cx + q[0] * r, y = cy + q[1] * r; if (i) c.lineTo(x, y); else c.moveTo(x, y); }); c.closePath(); c.fill(); c.restore();
        c.strokeStyle = '#e2574c'; c.lineWidth = 3; c.setLineDash([8, 6]);
        c.beginPath(); c.moveTo(cx + Math.sin(a) * r * 1.4, cy - Math.cos(a) * r * 1.4); c.lineTo(cx - Math.sin(a) * r * 1.4, cy + Math.cos(a) * r * 1.4); c.stroke(); c.setLineDash([]);
        c.fillStyle = '#2a2a2e'; c.font = '700 13px Nunito, sans-serif'; c.textAlign = 'center'; c.fillText('Drag anywhere to turn the mirror line. Yellow is the flipped copy.', W / 2, H - 14);
        break;
      }
      case 'angle-sort': case 'protractor': {
        const deg = act.degrees;
        const ox = act.kind === 'protractor' ? W / 2 : W / 2 - r * 0.5, oy = act.kind === 'protractor' ? H * 0.72 : cy + r * 0.5;
        const len = r * 1.3;
        if (act.kind === 'protractor') {
          c.strokeStyle = '#9a9aa3'; c.lineWidth = 2; c.beginPath(); c.arc(ox, oy, len, Math.PI, 2 * Math.PI); c.stroke();
          c.font = '700 11px Nunito, sans-serif'; c.fillStyle = '#2a2a2e'; c.textAlign = 'center'; c.textBaseline = 'middle';
          for (let d = 0; d <= 180; d += 10) { const a = Math.PI - d * Math.PI / 180; const x1 = ox + Math.cos(a) * len, y1 = oy - Math.sin(a) * len; const inner = d % 30 === 0 ? 0.88 : 0.94; c.beginPath(); c.moveTo(x1, y1); c.lineTo(ox + Math.cos(a) * len * inner, oy - Math.sin(a) * len * inner); c.stroke(); if (d % 30 === 0) c.fillText(String(d), ox + Math.cos(a) * len * 0.8, oy - Math.sin(a) * len * 0.8); }
          // her reading hand
          const ha = Math.PI - protractorHand * Math.PI / 180;
          c.strokeStyle = '#f5c542'; c.lineWidth = 4; c.beginPath(); c.moveTo(ox, oy); c.lineTo(ox + Math.cos(ha) * len * 1.05, oy - Math.sin(ha) * len * 1.05); c.stroke();
          c.fillStyle = '#2a2a2e'; c.font = '800 14px Nunito, sans-serif'; c.fillText(`hand: ${protractorHand}°`, W - 50, 20);
        }
        c.strokeStyle = '#2a2a2e'; c.lineWidth = 4; c.lineCap = 'round';
        c.beginPath(); c.moveTo(ox, oy); c.lineTo(ox + len, oy); c.stroke();
        const a2 = Math.PI - deg * Math.PI / 180;
        c.beginPath(); c.moveTo(ox, oy); c.lineTo(ox + Math.cos(a2) * len, oy - Math.sin(a2) * len); c.stroke();
        if (deg === 90) { c.strokeStyle = '#e2574c'; c.lineWidth = 2; c.strokeRect(ox, oy - 16, 16, 16); }
        else { c.strokeStyle = '#e2574c'; c.lineWidth = 2; c.beginPath(); c.arc(ox, oy, 24, -deg * Math.PI / 180, 0); c.stroke(); }
        break;
      }
      case 'perimeter': case 'area-count': {
        const g = gridGeom();
        for (const [x, y] of g.cells) {
          const k = `${x},${y}`;
          c.fillStyle = counted.has(k) ? '#f5c542' : '#5fbf6a';
          c.fillRect(g.ox + x * g.unit, g.oy + y * g.unit, g.unit, g.unit);
          c.strokeStyle = 'rgba(0,0,0,.25)'; c.lineWidth = 1; c.strokeRect(g.ox + x * g.unit, g.oy + y * g.unit, g.unit, g.unit);
        }
        // outline
        c.strokeStyle = '#2a2a2e'; c.lineWidth = 3;
        const set = new Set(g.cells.map(cc => cc.join(',')));
        for (const [x, y] of g.cells) {
          const X = g.ox + x * g.unit, Y = g.oy + y * g.unit;
          c.beginPath();
          if (!set.has(`${x},${y - 1}`)) { c.moveTo(X, Y); c.lineTo(X + g.unit, Y); }
          if (!set.has(`${x},${y + 1}`)) { c.moveTo(X, Y + g.unit); c.lineTo(X + g.unit, Y + g.unit); }
          if (!set.has(`${x - 1},${y}`)) { c.moveTo(X, Y); c.lineTo(X, Y + g.unit); }
          if (!set.has(`${x + 1},${y}`)) { c.moveTo(X + g.unit, Y); c.lineTo(X + g.unit, Y + g.unit); }
          c.stroke();
        }
        c.fillStyle = '#2a2a2e'; c.font = '700 13px Nunito, sans-serif'; c.textAlign = 'center'; c.fillText('Each square is 1 unit on a side.', W / 2, 16);
        break;
      }
      case 'area-rect': {
        const unit = Math.min((W - 60) / act.w, (H - 70) / act.h);
        const x0 = (W - unit * act.w) / 2, y0 = (H - unit * act.h) / 2 + 10;
        c.fillStyle = '#4f8fe6'; c.fillRect(x0, y0, unit * act.w, unit * act.h);
        c.strokeStyle = 'rgba(255,255,255,.35)'; c.lineWidth = 1;
        for (let i = 1; i < act.w; i++) { c.beginPath(); c.moveTo(x0 + i * unit, y0); c.lineTo(x0 + i * unit, y0 + unit * act.h); c.stroke(); }
        for (let j = 1; j < act.h; j++) { c.beginPath(); c.moveTo(x0, y0 + j * unit); c.lineTo(x0 + unit * act.w, y0 + j * unit); c.stroke(); }
        c.fillStyle = '#2a2a2e'; c.font = '800 15px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
        c.fillText(`${act.w}`, x0 + unit * act.w / 2, y0 - 14); c.fillText(`${act.h}`, x0 - 16, y0 + unit * act.h / 2);
        break;
      }
      case 'area-tri': {
        const unit = Math.min((W - 60) / act.a, (H - 70) / act.b);
        const x0 = (W - unit * act.a) / 2, y0 = (H - unit * act.b) / 2 + 10;
        c.fillStyle = '#9b6be6'; c.beginPath(); c.moveTo(x0, y0 + unit * act.b); c.lineTo(x0 + unit * act.a, y0 + unit * act.b); c.lineTo(x0, y0); c.closePath(); c.fill();
        c.strokeStyle = 'rgba(0,0,0,.2)'; c.lineWidth = 1; c.setLineDash([4, 4]); c.strokeRect(x0, y0, unit * act.a, unit * act.b); c.setLineDash([]);
        c.strokeStyle = '#e2574c'; c.lineWidth = 2; c.strokeRect(x0, y0 + unit * act.b - 14, 14, 14);
        c.fillStyle = '#2a2a2e'; c.font = '800 15px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
        c.fillText(`${act.a}`, x0 + unit * act.a / 2, y0 + unit * act.b + 14); c.fillText(`${act.b}`, x0 - 16, y0 + unit * act.b / 2);
        break;
      }
      case 'plot': case 'distance': {
        const g = planeGeom();
        c.strokeStyle = '#c9c4b4'; c.lineWidth = 1;
        for (let i = 0; i <= g.size; i++) { c.beginPath(); c.moveTo(g.ox + i * g.unit, g.oy); c.lineTo(g.ox + i * g.unit, g.oy - g.size * g.unit); c.stroke(); c.beginPath(); c.moveTo(g.ox, g.oy - i * g.unit); c.lineTo(g.ox + g.size * g.unit, g.oy - i * g.unit); c.stroke(); }
        c.strokeStyle = '#2a2a2e'; c.lineWidth = 2; c.beginPath(); c.moveTo(g.ox, g.oy); c.lineTo(g.ox + g.size * g.unit, g.oy); c.moveTo(g.ox, g.oy); c.lineTo(g.ox, g.oy - g.size * g.unit); c.stroke();
        c.fillStyle = '#2a2a2e'; c.font = '700 11px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
        for (let i = 0; i <= g.size; i++) { c.fillText(String(i), g.ox + i * g.unit, g.oy + 12); c.fillText(String(i), g.ox - 12, g.oy - i * g.unit); }
        if (act.kind === 'distance') {
          const pt = (p: Pt, col: string, label: string) => { c.fillStyle = col; c.beginPath(); c.arc(g.ox + p[0] * g.unit, g.oy - p[1] * g.unit, 7, 0, 7); c.fill(); c.fillStyle = '#2a2a2e'; c.font = '800 14px Nunito, sans-serif'; c.fillText(label, g.ox + p[0] * g.unit + 12, g.oy - p[1] * g.unit - 12); };
          pt(act.a, '#e2574c', 'A'); pt(act.b, '#4f8fe6', 'B');
        } else if (plotted) { c.fillStyle = '#f5c542'; c.beginPath(); c.arc(g.ox + plotted[0] * g.unit, g.oy - plotted[1] * g.unit, 8, 0, 7); c.fill(); c.strokeStyle = '#2a2a2e'; c.lineWidth = 2; c.stroke(); }
        break;
      }
      case 'volume': {
        // a stack of layers drawn as offset grids
        const unit = Math.min((W - 80) / (act.l + act.h * 0.5), (H - 60) / (act.w + act.h * 0.5));
        const x0 = 30, y0 = H - 30 - unit * act.w;
        for (let layer = 0; layer < act.h; layer++) {
          const ox = x0 + layer * unit * 0.5, oy = y0 - layer * unit * 0.5;
          c.fillStyle = layer === act.h - 1 ? '#f5c542' : '#e0b07a';
          for (let i = 0; i < act.l; i++) for (let j = 0; j < act.w; j++) { c.fillRect(ox + i * unit, oy + j * unit, unit, unit); c.strokeStyle = 'rgba(0,0,0,.35)'; c.lineWidth = 1; c.strokeRect(ox + i * unit, oy + j * unit, unit, unit); }
        }
        c.fillStyle = '#2a2a2e'; c.font = '800 14px Nunito, sans-serif'; c.textAlign = 'left'; c.textBaseline = 'top';
        c.fillText(`${act.l} long, ${act.w} wide, ${act.h} layer${act.h > 1 ? 's' : ''} tall`, 12, 10);
        break;
      }
    }
    void roundRect;
  }

  newActivity();
  return { destroy() { unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.shape-lab',
  title: 'Shape Lab',
  blurb: 'Name shapes, find mirror lines, count edges and squares, measure angles and boxes.',
  category: 'math',
  skills: ['geometry'],
  levelRange: [1, 10],
  unlock: { stars: 12 },
  mount,
};
export default mod;
